// src/components/user/ReconcileLinkModal.jsx
//
// Reconciliation step for the directory ↔ group link. Given a directory listing and a group,
// it diffs the shared fields (name, description, boundary), lets the Steward pick which side
// wins per differing field, then writes through and establishes the link
// (claimed_by_group_id + claim_status = 'verified').
//
// Steward-scoped: group writes rely on the communities owner/community-admin UPDATE RLS, so
// callers scope the group choice to groups the Steward owns. Listing writes rely on is_steward.
//
// Invoked from:
//   • StewardWorkspace — "Link to existing group" flow (pick a group → reconcile).
//   • AddEditGroup     — create-from-prefill save, when the new group diverges from the listing.

import React, { useState, useEffect } from 'react';
import Modal from '../core/Modal';
import { useTranslation } from '../../i18n/useTranslation';
import { updateListing, updateGroupSharedFields, getBoundaryNames } from '../../data/directoryService';

const norm = (v) => (v == null ? '' : String(v).trim());

const ReconcileLinkModal = ({ listing, group, onClose, onDone }) => {
  const { t } = useTranslation('steward');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [boundaryNames, setBoundaryNames] = useState({});

  const listingBoundary = listing?.boundary_id ?? null;
  const groupBoundaries = Array.isArray(group?.boundary_ids) ? group.boundary_ids : [];
  const groupBoundary = groupBoundaries[0] ?? null;
  // Boundary is only cleanly reconcilable when the listing has an area and the group has EXACTLY
  // one boundary:
  //  • national listing (no area, 20261004f) → "listing wins" would write [null] into the group's
  //    areas and "group wins" would give a national listing an area (Roadmap #87, R7 precondition).
  //  • >1 → "group wins" is ambiguous and "listing wins" would silently drop the others.
  //  • 0  → "group wins" would null the listing's boundary_id, which a listing with an area must keep.
  // In each case we skip the boundary row and leave both sides untouched.
  const boundaryReconcilable = listingBoundary != null && groupBoundaries.length === 1;

  const diffs = {
    name: norm(listing?.name) !== norm(group?.name),
    description: norm(listing?.description) !== norm(group?.description),
    boundary: boundaryReconcilable && norm(listingBoundary) !== norm(groupBoundary)
  };
  const FIELDS = [
    { key: 'name', label: t('reconcile_field_name') },
    { key: 'description', label: t('reconcile_field_description') },
    { key: 'boundary', label: t('reconcile_field_boundary') }
  ];
  const changedFields = FIELDS.filter(f => diffs[f.key]);

  // Default every choice to the group (group = source of truth once linked).
  const [choices, setChoices] = useState({ name: 'group', description: 'group', boundary: 'group' });

  useEffect(() => {
    getBoundaryNames([listingBoundary, groupBoundary]).then(setBoundaryNames);
  }, [listingBoundary, groupBoundary]);

  const boundaryLabel = (id) => (id == null ? t('reconcile_no_boundary') : (boundaryNames[id] || `#${id}`));
  const valueFor = (fieldKey, side) => {
    if (fieldKey === 'boundary') return boundaryLabel(side === 'group' ? groupBoundary : listingBoundary);
    const v = side === 'group' ? group?.[fieldKey] : listing?.[fieldKey];
    return norm(v) || '—';
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    // Always establish the link on the listing side; add reconciled field winners as we go.
    const listingPatch = { claimed_by_group_id: group.id, claim_status: 'verified' };
    const groupPatch = {};
    changedFields.forEach(({ key }) => {
      const winner = choices[key];
      if (key === 'boundary') {
        if (winner === 'group') listingPatch.boundary_id = groupBoundary;
        else groupPatch.boundary_ids = [listingBoundary];
      } else if (winner === 'group') {
        listingPatch[key] = group?.[key] ?? null;
      } else {
        groupPatch[key] = listing?.[key] ?? null;
      }
    });

    const r1 = await updateListing(listing.id, listingPatch);
    let r2 = { success: true };
    if (Object.keys(groupPatch).length > 0) {
      r2 = await updateGroupSharedFields(group.id, groupPatch);
    }
    setSaving(false);
    if (!r1.success || !r2.success) {
      setError(t('reconcile_error'));
      return;
    }
    window.dispatchEvent(new CustomEvent('ournet:directory-changed'));
    onDone?.();
  };

  const colHeadStyle = {
    fontFamily: 'OpenSauceSans, sans-serif', fontSize: '11px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.03em', color: '#6b7280'
  };
  const optionStyle = (active) => ({
    flex: 1, padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
    border: active ? '2px solid #7c3aed' : '1px solid #d1d5db',
    background: active ? '#f5f3ff' : 'white',
    fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px',
    color: active ? '#5b21b6' : '#374151', textAlign: 'left'
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      closeOnEsc={!saving}
      closeOnBackdrop={() => !saving}
      panelStyle={{
        background: 'white', borderRadius: '12px', padding: '24px',
        width: '90%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)', fontFamily: 'OpenSauceSans, sans-serif'
      }}
    >
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#024B99', marginTop: 0, marginBottom: '6px' }}>
          {t('reconcile_title')}
        </h2>
        <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0, marginBottom: '18px' }}>
          {changedFields.length === 0 ? t('reconcile_no_conflicts') : t('reconcile_intro')}
        </p>

        {changedFields.map(({ key, label }) => (
          <div key={key} style={{ marginBottom: '16px' }}>
            <div style={{ ...colHeadStyle, marginBottom: '6px', color: '#374151', fontSize: '12px' }}>{label}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={() => setChoices(p => ({ ...p, [key]: 'group' }))} style={optionStyle(choices[key] === 'group')}>
                <div style={colHeadStyle}>{t('reconcile_use_group')}</div>
                <div style={{ marginTop: '3px', wordBreak: 'break-word' }}>{valueFor(key, 'group')}</div>
              </button>
              <button type="button" onClick={() => setChoices(p => ({ ...p, [key]: 'listing' }))} style={optionStyle(choices[key] === 'listing')}>
                <div style={colHeadStyle}>{t('reconcile_use_listing')}</div>
                <div style={{ marginTop: '3px', wordBreak: 'break-word' }}>{valueFor(key, 'listing')}</div>
              </button>
            </div>
          </div>
        ))}

        {groupBoundaries.length > 1 && (
          <div style={{ marginBottom: '16px', padding: '8px 10px', background: '#fffbeb', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
            {t('reconcile_boundary_multi')}
          </div>
        )}

        {error && (
          <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '12px', color: '#dc2626' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{
            padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
            background: 'white', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#6b7280', cursor: 'pointer'
          }}>
            {t('common:cancel')}
          </button>
          <button type="button" onClick={handleConfirm} disabled={saving} style={{
            padding: '8px 16px', border: 'none', borderRadius: '6px',
            background: '#7c3aed', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', fontWeight: '600', color: 'white', cursor: 'pointer'
          }}>
            {saving ? t('reconcile_linking') : t('reconcile_confirm')}
          </button>
        </div>
    </Modal>
  );
};

export default ReconcileLinkModal;
